import { consoleRoutes, WORKFLOW_LIMITS, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, Segmented, Skeleton, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useNavigate } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../session/workspace-context";
import { RefsProvider } from "./builder/refs";
import { needsStartOptions, startOptionsReady, TriggerStartOptions } from "./builder/trigger-options-p5";
import { starterFor } from "./starter";
import { TemplateGallery, templateAllowed } from "./template-gallery";
import { optionsFor, resolveChoices, useTemplateLibrary, useTemplateOptions } from "./template-options";
import { TemplateRequirements, TemplateSetupNote } from "./template-requirements";
import { fillTemplate, localized, missingRequirements, type PlaceholderName, type TemplateChoices } from "./templates";
import { TriggerPicker } from "./trigger-picker";
import type { TriggerOptions } from "./types";
import { useWorkflowMutations } from "./use-workflows";

const C = "engage.workflows.create";
export type CreateMode = "trigger" | "template";

interface CreateFormProps {
  initialMode: CreateMode;
  /** The Templates button: only the library, no name or trigger (the draft takes the template's name). */
  templatesOnly: boolean;
  onClose: () => void;
}

function CreateForm({ initialMode, templatesOnly, onClose }: CreateFormProps) {
  const { t, i18n } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const plan = useWorkspaceDetails().data?.plan;
  const { create } = useWorkflowMutations();
  const library = useTemplateLibrary();
  const { options: allOptions, connectorEndpoints, loading } = useTemplateOptions();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [mode, setMode] = useState<CreateMode>(initialMode);
  const [trigger, setTrigger] = useState<WorkflowTrigger>("booking_confirmed");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [picked, setPicked] = useState<TemplateChoices>({});
  const [startOptions, setStartOptions] = useState<TriggerOptions>({});

  const templates = library.data?.templates ?? [];
  const entry = templates.find((candidate) => candidate.id === templateId) ?? templates.find((candidate) => templateAllowed(candidate, plan));
  const options = entry ? optionsFor(entry, allOptions, connectorEndpoints) : allOptions;
  const missing = entry ? missingRequirements(entry, options) : [];
  const templateReady = entry !== undefined && templateAllowed(entry, plan) && !loading && missing.length === 0;
  const triggerReady = startOptionsReady(trigger, startOptions);
  const chooseTrigger = (next: WorkflowTrigger) => {
    setTrigger(next);
    setStartOptions({});
  };

  /** A trigger needs a name of its own; a template falls back to its own name. Either way the name fits the limit. */
  const nameProblem = (): string | null => {
    const trimmed = name.trim();
    if (trimmed.length > WORKFLOW_LIMITS.name) return t(`${C}.nameTooLong`, { max: WORKFLOW_LIMITS.name });
    if (!templatesOnly && mode === "trigger" && trimmed === "") return t(`${C}.nameRequired`);
    return null;
  };
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const problem = nameProblem();
    if (problem) {
      setNameError(problem);
      return;
    }
    const fromTemplate = mode === "template";
    if (fromTemplate && (!entry || !library.data)) return;
    const chosen = fromTemplate && entry ? entry.trigger : trigger;
    const title = name.trim() || (fromTemplate && entry ? localized(entry.name, i18n.language) : t(`engage.workflows.triggers.${trigger}`));
    const definition = fromTemplate && entry && library.data ? fillTemplate(entry, library.data.placeholders, resolveChoices(entry.requires, options, picked)) : starterFor(trigger, t, needsStartOptions(trigger) ? startOptions : {});
    const description = fromTemplate && entry ? localized(entry.description, i18n.language) : undefined;
    create.mutate({ name: title, trigger: chosen, ...(description ? { description } : {}), ...(definition ? { definition } : {}) }, {
      onSuccess: (workflow) => {
        toast.success(t(`${C}.created`));
        void navigate(consoleRoutes.workflow(summary.slug, workflow.id));
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  const pick = (placeholder: PlaceholderName, id: string) => { setPicked((current) => ({ ...current, [placeholder]: id })); };
  return (
    <form className="space-y-5" onSubmit={submit}>
      {templatesOnly ? null : (
        <>
          <Field label={t(`${C}.name`)} error={nameError ?? undefined}>
            <Input placeholder={t(`${C}.namePlaceholder`)} value={name} onChange={(e) => { setName(e.target.value); setNameError(null); }} />
          </Field>
          <Segmented
            aria-label={t(`${C}.mode`)}
            value={mode}
            onValueChange={setMode}
            options={[{ value: "trigger", label: t(`${C}.fromTrigger`) }, { value: "template", label: t(`${C}.fromTemplate`) }]}
          />
        </>
      )}
      {mode === "trigger" ? (
        <>
          <TriggerPicker value={trigger} onChange={chooseTrigger} />
          <TriggerStartOptions trigger={trigger} options={startOptions} onChange={setStartOptions} />
        </>
      ) : (
        <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
          <TemplateGallery value={entry?.id ?? ""} onChange={setTemplateId} showNote={!templatesOnly} />
          {entry && loading ? <Skeleton className="h-20 rounded-lg" aria-hidden="true" /> : null}
          {entry && !loading ? <TemplateRequirements entry={entry} options={options} picked={picked} onPick={pick} /> : null}
          {entry ? <TemplateSetupNote entry={entry} /> : null}
        </div>
      )}
      <div className="flex justify-end gap-2 border-t border-line pt-3">
        <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
        <Button type="submit" loading={create.isPending} disabled={mode === "template" ? !templateReady : !triggerReady}>{t(`${C}.submit`)}</Button>
      </div>
    </form>
  );
}

/** Name + a trigger (grouped like the list page) or a template from the library; creates a draft and opens it. */
export function CreateWorkflowDialog({ onClose, initialMode = "trigger" }: { onClose: () => void; initialMode?: CreateMode }) {
  const { t } = useTranslation();
  const templatesOnly = initialMode === "template";
  return (
    <Dialog open onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        title={templatesOnly ? t(`${C}.templatesTitle`) : t(`${C}.title`)}
        description={templatesOnly ? t("engage.workflows.templates.note") : t(`${C}.subtitle`)}
        closeLabel={t("common.close")}
        className="max-w-3xl"
      >
        <RefsProvider>
          <CreateForm initialMode={initialMode} templatesOnly={templatesOnly} onClose={onClose} />
        </RefsProvider>
      </DialogContent>
    </Dialog>
  );
}
